import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase-auth'
import { useAuth } from '../../contexts/AuthContextLazy'
import { 
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentCheckIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
// For now, we'll implement drag and drop later
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

interface Task {
  id: string
  title: string
  description: string
  task_type: 'task' | 'milestone' | 'decision'
  duration_days: number
  depends_on: string[]
  compliance_required: boolean
  compliance_type?: string
  requires_signature: boolean
  position: number
}

interface TemplateEditorProps {
  template?: any
  onSave: () => void
  onCancel: () => void
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({ 
  template, 
  onSave, 
  onCancel 
}) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_type: 'other',
    icon: '',
    default_duration_days: 30,
    is_active: true
  })
  const [milestones, setMilestones] = useState<any[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'milestones' | 'tasks'>('info')

  const projectTypes = [
    { value: 'insurance_policy', label: 'Insurance Policy', icon: '🛡️' },
    { value: 'investment_transfer', label: 'Investment Transfer', icon: '💼' },
    { value: 'estate_planning', label: 'Estate Planning', icon: '📋' },
    { value: 'tax_planning', label: 'Tax Planning', icon: '📊' },
    { value: 'retirement_planning', label: 'Retirement Planning', icon: '🎯' },
    { value: 'education_planning', label: 'Education Planning', icon: '🎓' },
    { value: 'business_planning', label: 'Business Planning', icon: '🏢' },
    { value: 'other', label: 'Other', icon: '📁' }
  ]

  const taskTypes = [
    { value: 'task', label: 'Task', color: 'blue' },
    { value: 'milestone', label: 'Milestone', color: 'green' },
    { value: 'decision', label: 'Decision Point', color: 'yellow' }
  ]

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        project_type: template.project_type,
        icon: template.icon || '',
        default_duration_days: template.default_duration_days || 30,
        is_active: template.is_active !== false
      })
      setMilestones(template.milestones || [])
      setTasks(template.tasks || [])
    }
  }, [template])

  const handleAddMilestone = () => {
    const newMilestone = {
      id: `milestone-${Date.now()}`,
      name: '',
      sequence: milestones.length + 1,
      description: '',
      duration_days: 7
    }
    setMilestones([...milestones, newMilestone])
  }

  const handleUpdateMilestone = (index: number, field: string, value: any) => {
    const updated = [...milestones]
    updated[index] = { ...updated[index], [field]: value }
    setMilestones(updated)
  }

  const handleDeleteMilestone = (index: number) => {
    const updated = milestones.filter((_, i) => i !== index)
    // Resequence remaining milestones
    updated.forEach((m, i) => {
      m.sequence = i + 1
    })
    setMilestones(updated)
  }

  const handleAddTask = () => {
    const newTask: Task = {
      id: `task-${Date.now()}`,
      title: '',
      description: '',
      task_type: 'task',
      duration_days: 1,
      depends_on: [],
      compliance_required: false,
      requires_signature: false,
      position: tasks.length
    }
    setTasks([...tasks, newTask])
  }

  const handleUpdateTask = (index: number, field: string, value: any) => {
    const updated = [...tasks]
    updated[index] = { ...updated[index], [field]: value }
    setTasks(updated)
  }

  const handleDeleteTask = (index: number) => {
    const taskId = tasks[index].id
    const updated = tasks.filter((_, i) => i !== index)
    // Remove this task from any dependencies
    updated.forEach(task => {
      task.depends_on = task.depends_on.filter(id => id !== taskId)
    })
    // Reposition remaining tasks
    updated.forEach((t, i) => {
      t.position = i
    })
    setTasks(updated)
  }

  const moveTaskUp = (index: number) => {
    if (index === 0) return
    const updated = [...tasks]
    const temp = updated[index - 1]
    updated[index - 1] = updated[index]
    updated[index] = temp
    // Update positions
    updated.forEach((t, i) => {
      t.position = i
    })
    setTasks(updated)
  }

  const moveTaskDown = (index: number) => {
    if (index === tasks.length - 1) return
    const updated = [...tasks]
    const temp = updated[index + 1]
    updated[index + 1] = updated[index]
    updated[index] = temp
    // Update positions
    updated.forEach((t, i) => {
      t.position = i
    })
    setTasks(updated)
  }

  const calculateTotalDuration = () => {
    const milestoneDays = milestones.reduce((sum, m) => sum + (m.duration_days || 0), 0)
    return milestoneDays
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      const templateData = {
        ...formData,
        milestones,
        tasks,
        created_by: template ? template.created_by : user?.id,
        updated_at: new Date().toISOString()
      }

      if (template) {
        // Update existing template
        const { error } = await supabase
          .from('project_templates')
          .update(templateData)
          .eq('id', template.id)

        if (error) throw error
      } else {
        // Create new template
        const { error } = await supabase
          .from('project_templates')
          .insert({
            ...templateData,
            id: `template-${Date.now()}`,
            usage_count: 0,
            created_at: new Date().toISOString()
          })

        if (error) throw error
      }

      onSave()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error saving template:', error)
      }
    } finally {
      setSaving(false)
    }
  }

  const getProjectTypeIcon = (type: string) => {
    return projectTypes.find(pt => pt.value === type)?.icon || '📁'
  }

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onCancel}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {template ? 'Edit Template' : 'Create New Template'}
                </h3>
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('info')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'info'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Basic Information
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('milestones')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'milestones'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Milestones ({milestones.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('tasks')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'tasks'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Tasks ({tasks.length})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {activeTab === 'info' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Template Name
                      </label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe what this template is for..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Project Type
                        </label>
                        <select
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                          value={formData.project_type}
                          onChange={(e) => {
                            const type = e.target.value
                            setFormData({ 
                              ...formData, 
                              project_type: type,
                              icon: formData.icon || getProjectTypeIcon(type)
                            })
                          }}
                        >
                          {projectTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Icon (Emoji)
                        </label>
                        <input
                          type="text"
                          maxLength={2}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                          value={formData.icon}
                          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                          placeholder={getProjectTypeIcon(formData.project_type)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Default Duration (days)
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                          value={formData.default_duration_days}
                          onChange={(e) => setFormData({ ...formData, default_duration_days: parseInt(e.target.value) })}
                        />
                      </div>

                      <div className="flex items-end">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          />
                          <span className="ml-2 text-sm text-gray-900">Active</span>
                        </label>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        <strong>Calculated Duration:</strong> {calculateTotalDuration()} days based on milestones
                      </p>
                    </div>
                  </>
                )}

                {activeTab === 'milestones' && (
                  <div className="space-y-4">
                    {milestones.map((milestone, index) => (
                      <div key={milestone.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Milestone {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDeleteMilestone(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Milestone name"
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                            value={milestone.name}
                            onChange={(e) => handleUpdateMilestone(index, 'name', e.target.value)}
                            required
                          />
                          <input
                            type="number"
                            placeholder="Duration (days)"
                            min="1"
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                            value={milestone.duration_days}
                            onChange={(e) => handleUpdateMilestone(index, 'duration_days', parseInt(e.target.value))}
                            required
                          />
                        </div>
                        <textarea
                          placeholder="Description"
                          rows={2}
                          className="mt-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                          value={milestone.description}
                          onChange={(e) => handleUpdateMilestone(index, 'description', e.target.value)}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddMilestone}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      Add Milestone
                    </button>
                  </div>
                )}

                {activeTab === 'tasks' && (
                  <div className="space-y-4">
                    {tasks.map((task, index) => (
                      <div
                        key={task.id}
                        className="border border-gray-200 rounded-lg p-4 bg-white"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <select
                            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                            value={task.task_type}
                            onChange={(e) => handleUpdateTask(index, 'task_type', e.target.value)}
                          >
                            {taskTypes.map(type => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() => moveTaskUp(index)}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              title="Move up"
                            >
                              <ChevronUpIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveTaskDown(index)}
                              disabled={index === tasks.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              title="Move down"
                            >
                              <ChevronDownIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTask(index)}
                              className="p-1 text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <input
                            type="text"
                            placeholder="Task title"
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                            value={task.title}
                            onChange={(e) => handleUpdateTask(index, 'title', e.target.value)}
                            required
                          />
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              placeholder="Days"
                              min="1"
                              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                              value={task.duration_days}
                              onChange={(e) => handleUpdateTask(index, 'duration_days', parseInt(e.target.value))}
                              required
                            />
                          </div>
                        </div>

                        <textarea
                          placeholder="Description"
                          rows={2}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 mb-2"
                          value={task.description}
                          onChange={(e) => handleUpdateTask(index, 'description', e.target.value)}
                        />

                        <div className="flex items-center space-x-4 text-sm">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={task.compliance_required}
                              onChange={(e) => handleUpdateTask(index, 'compliance_required', e.target.checked)}
                            />
                            <span className="ml-2 text-gray-700">Compliance Required</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={task.requires_signature}
                              onChange={(e) => handleUpdateTask(index, 'requires_signature', e.target.checked)}
                            />
                            <span className="ml-2 text-gray-700">Requires Signature</span>
                          </label>
                        </div>

                        {task.compliance_required && (
                          <input
                            type="text"
                            placeholder="Compliance type (e.g., KYC, AML)"
                            className="mt-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900"
                            value={task.compliance_type || ''}
                            onChange={(e) => handleUpdateTask(index, 'compliance_type', e.target.value)}
                          />
                        )}
                      </div>
                    ))}
                  
                    <button
                      type="button"
                      onClick={handleAddTask}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                      Add Task
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={saving}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}